// AufrufDisplayPage.jsx — Kämpfer-Aufruf Display (Standalone, kein Login)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import LanguageSwitch from '../components/LanguageSwitch';
import './AufrufDisplayPage.css';
import { wanduhrzeit } from '../lib/wanduhrzeit';
import { kategorieText } from '../lib/kategorieText';
import { useHervorheben, useAnimierteListe } from '../hooks/useHervorheben';

// TDA Live — öffentliche App für Zuschauer, Eltern und Sportler
// (Quelle ~/live-tda). ?t=<id>&tab=suche öffnet direkt die Athletensuche
// dieses Turniers.
const LIVE_URL = 'https://live.tda-intl.org';

// Verzug erst ab 5 Minuten zeigen — darunter ist es Rauschen, und ein
// Schirm, der dauernd "+2 min" meldet, wird nicht mehr gelesen.
const VERZUG_AB_MIN = 5;

function Verzug({ minuten, planzeit, t, gross }) {
  if (minuten == null || Math.abs(minuten) < VERZUG_AB_MIN) return null;
  const spaeter = minuten > 0;
  return (
    <span className={`adp-verzug adp-verzug--${spaeter ? 'spaeter' : 'frueher'}${gross ? ' adp-verzug--gross' : ''}`}
          title={planzeit ? t('aufruf.plan', { zeit: planzeit }) : undefined}>
      {t(spaeter ? 'aufruf.later' : 'aufruf.earlier', { n: Math.abs(minuten) })}
      {gross && planzeit && <span className="adp-verzug-plan"> · {t('aufruf.plan', { zeit: planzeit })}</span>}
    </span>
  );
}

/** "2026-09-25T07:30" → "07:30" bzw. "Fr 25.09." — Text, keine Zeitzone */
const hhmm = (s) => (s ? s.slice(11, 16) : '');
function tagKurz(s) {
  if (!s) return '';
  const [j, m, d] = s.slice(0, 10).split('-').map(Number);
  const wt = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][new Date(j, m - 1, d).getDay()];
  return `${wt} ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.`;
}

/**
 * Infoleiste unten: Einwaage, Hinweise des Veranstalters (wechseln alle 8 s)
 * und der QR-Code zu TDA Live. Gepflegt in /aufruf-control/:turnierId.
 */
function InfoLeiste({ info, turnierId, t }) {
  const [qr, setQr] = useState('');
  const [nr, setNr] = useState(0);
  const liveLink = `${LIVE_URL}/?t=${turnierId}&tab=suche`;

  useEffect(() => {
    QRCode.toString(liveLink, { type: 'svg', margin: 1, errorCorrectionLevel: 'M',
                                color: { dark: '#000000', light: '#ffffff' } })
      .then(setQr).catch(() => setQr(''));
  }, [liveLink]);

  const meldungen = info?.meldungen ?? [];
  useEffect(() => {
    if (meldungen.length < 2) { setNr(0); return undefined; }
    const id = setInterval(() => setNr(n => (n + 1) % meldungen.length), 8000);
    return () => clearInterval(id);
  }, [meldungen.length]);

  const ew = info?.einwaage;
  let einwaage = null;
  if (ew && ew.zustand === 'offen') einwaage = t('aufruf.weigh_open', { bis: hhmm(ew.bis) });
  else if (ew && ew.zustand === 'kommt') {
    einwaage = ew.heute
      ? t('aufruf.weigh_soon', { von: hhmm(ew.von) })
      : t('aufruf.weigh_day', { tag: tagKurz(ew.von), von: hhmm(ew.von), bis: hhmm(ew.bis) });
  }

  return (
    <footer className="adp-info">
      <div className="adp-info-text">
        {einwaage && (
          <div className={`adp-info-einwaage${ew.zustand === 'offen' ? ' offen' : ''}`}>{einwaage}</div>
        )}
        {meldungen.length > 0 ? (
          <div className="adp-info-meldung" key={nr}>
            <span className="adp-info-lbl">{t('aufruf.info')}</span>
            <span className="adp-info-satz">{meldungen[nr % meldungen.length]}</span>
            {meldungen.length > 1 && (
              <span className="adp-info-zaehler">{(nr % meldungen.length) + 1}/{meldungen.length}</span>
            )}
          </div>
        ) : !einwaage && (
          <div className="adp-info-fuss">TDA International · Kämpfer-Aufruf</div>
        )}
      </div>
      {qr && (
        <div className="adp-info-qr">
          <div className="adp-info-qr-text">
            <strong>{t('aufruf.live_qr')}</strong>
            <span>live.tda-intl.org</span>
          </div>
          {/* SVG kommt aus der qrcode-Bibliothek, nicht aus Nutzereingaben */}
          <div className="adp-info-qr-bild" dangerouslySetInnerHTML={{ __html: qr }} />
        </div>
      )}
    </footer>
  );
}

const GURT_COLOR = {
  'Weiß':    '#f0f0f0', 'Gelb':   '#f5d200', 'Orange': '#f08000',
  'Grün':    '#2d8a2d', 'Blau':   '#1a5fa8', 'Rot':    '#cc1a1a',
  'Braun':   '#7a4a1a', 'Schwarz':'#1a1a1a', 'Dan 1':  '#1a1a1a',
};

// kaempfe.runde zählt AUFWÄRTS (1 = erste Runde). Wie weit ein Kampf vom
// Finale weg ist, sagt erst die Rundenzahl des Brackets (runden_gesamt aus
// /aufruf). Vorher hieß Runde 1 „Finale“ und das Finale eines Vierer-Pools
// „Halbfinale“ (Gesamttest 18.10.2026).
function rundenName(kampf, t) {
  const runde = Number(kampf?.runde);
  if (kampf?.bracket_typ === 'stichkampf') return t('aufruf.tiebreak');
  if (Number(kampf?.kampf_nummer) === 99 && Number(kampf?.ist_trostrunde) === 1) return t('aufruf.third_place');
  // Jeder gegen jeden (3er-Pool): alle Kämpfe stehen in Runde 1, ohne diese
  // Ausnahme hieß jeder davon „Finale“. Je Kampf eine Runde.
  if (kampf?.tournament_mode === 'round_robin') return t('aufruf.round', { n: Number(kampf?.kampf_nummer) || runde });
  const gesamt = Number(kampf?.runden_gesamt);
  if (!Number(kampf?.ist_trostrunde) && Number.isFinite(gesamt) && gesamt >= runde) {
    const bisFinale = gesamt - runde;
    if (bisFinale === 0) return t('aufruf.final');
    if (bisFinale === 1) return t('aufruf.semifinal');
    if (bisFinale === 2) return t('aufruf.quarterfinal');
    if (bisFinale === 3) return t('aufruf.round_of_16');
  }
  return t('aufruf.round', { n: runde });
}

function GurtDot({ farbe }) {
  const col = GURT_COLOR[farbe] ?? '#888';
  return (
    <span
      className="adp-gurt-dot"
      style={{ background: col, border: farbe === 'Weiß' ? '1px solid #555' : 'none' }}
      title={farbe}
    />
  );
}

// Nur anzeigen wenn innerhalb von 2 Stunden — sonst null
function calcCountdownFromMs(diff) {
  if (diff > 7200000) return null;  // > 2h nicht anzeigen
  if (diff <= -90000) return null;
  if (diff <= 0) return { text: 'JETZT', state: 'now' };
  const totalSecs = Math.floor(diff / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  const text = `${mins}:${String(secs).padStart(2, '0')}`;
  if (diff < 120000)  return { text, state: 'urgent' };
  if (diff < 600000)  return { text, state: 'soon' };
  return { text, state: 'ok' };
}

function calcCountdown(isoStr, now) {
  const t = wanduhrzeit(isoStr);
  if (!t) return null;
  return calcCountdownFromMs(t - now);
}

function calcCountdownToTime(timeStr, turnierdatum, now) {
  if (!timeStr || !turnierdatum) return null;
  const datePart = String(turnierdatum).slice(0, 10);
  const target = new Date(`${datePart}T${timeStr}:00`);
  return calcCountdownFromMs(target - now);
}

function getSlotStatus(slot, turnierdatum, now) {
  if (!turnierdatum || !slot.start_time) return 'upcoming';
  const datePart = String(turnierdatum).slice(0, 10);
  const start = new Date(`${datePart}T${slot.start_time}:00`);
  if (now < start.getTime()) return 'upcoming';
  if (slot.end_time) {
    const end = new Date(`${datePart}T${slot.end_time}:00`);
    return now < end.getTime() ? 'active' : 'done';
  }
  return now < start.getTime() + 3600000 ? 'active' : 'done';
}

// ── Kleine Komponenten für die Grid-Ansicht ──────────────────────────────────

function FighterCard({ vorname, nachname, verein, gurt, side }) {
  return (
    <div className={`adp-fighter ${side}`}>
      <div className="adp-fighter-name">
        {vorname} {nachname}
        {gurt && <GurtDot farbe={gurt} />}
      </div>
      <div className="adp-fighter-club">{verein || '—'}</div>
    </div>
  );
}

function KampfRow({ kampf, t }) {
  return (
    <div className="adp-kampf-row">
      <div className="adp-kampf-meta">
        <span className="adp-kampf-runde">{rundenName(kampf, t)}</span>
        <span className="adp-kampf-kat">{kampf.bracket_name}</span>
      </div>
      <div className="adp-kampf-vs">
        {kampf.k1_vorname
          ? <FighterCard vorname={kampf.k1_vorname} nachname={kampf.k1_nachname} verein={kampf.k1_verein} gurt={kampf.k1_gurt} side="left" />
          : <div className="adp-fighter left adp-tbd">—</div>
        }
        <div className="adp-vs-badge">VS</div>
        {kampf.k2_vorname
          ? <FighterCard vorname={kampf.k2_vorname} nachname={kampf.k2_nachname} verein={kampf.k2_verein} gurt={kampf.k2_gurt} side="right" />
          : <div className="adp-fighter right adp-tbd">—</div>
        }
      </div>
    </div>
  );
}

// ── Je Matte nur „Jetzt“ und „Als Nächstes“ (18.10.2026) ───────────────────
// Vorher stand je Matte die ganze Warteschlange mit 13-px-Namen untereinander
// und lief im Ping-Pong durch — aus der Halle nicht lesbar, bei 1280 px
// überlappten sich die Namen. Jetzt: zwei Einträge, groß, Namen untereinander
// statt nebeneinander. Der Rest steht nur als Zahl da.

/** Warteschlange einer Matte: bereite Kämpfe zuerst, dann wartende, dann
 *  Zeitplan-Blöcke nach Uhrzeit. */
function matteWarteschlange({ nr, ready, pending, zeitplan, turnierdatum, now }) {
  const hier = (k) => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr);
  const nachNummer = (a, b) => {
    if (a.geplante_zeit && b.geplante_zeit) return wanduhrzeit(a.geplante_zeit) - wanduhrzeit(b.geplante_zeit);
    return (a.kampf_nummer ?? 0) - (b.kampf_nummer ?? 0);
  };
  const kaempfe = [
    ...ready.filter(hier).sort(nachNummer).map(k => ({ k, bereit: true })),
    ...pending.filter(hier).sort(nachNummer).map(k => ({ k, bereit: false })),
  ].map(({ k, bereit }) => ({ type: 'kampf', key: `k${k.kampf_id}`, bereit, data: k }));
  const slots = (zeitplan ?? [])
    .filter(s => Number(s.ring) === Number(nr))
    .filter(s => getSlotStatus(s, turnierdatum, now) !== 'done')
    .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''))
    .map(s => ({ type: 'slot', key: `s${s.id}`, bereit: getSlotStatus(s, turnierdatum, now) === 'active', data: s }));
  return [...kaempfe, ...slots];
}

// „Open/Creative/Music“ ist für eine Spalte bei 1280 px ein zu langes Wort;
// nach dem Schrägstrich darf es umbrechen (Nullbreiten-Leerzeichen).
const umbruchNachSchraegstrich = (text) => String(text).replace(/\//g, '/\u200B');

function Kaempfer({ vorname, nachname, verein, gurt }) {
  if (!vorname && !nachname) return <div className="adp-e-name adp-tbd">—</div>;
  return (
    <div className="adp-e-kaempfer">
      <div className="adp-e-name">
        {gurt && <GurtDot farbe={gurt} />}
        <span>{vorname} {nachname}</span>
      </div>
      {verein && <div className="adp-e-verein">{verein}</div>}
    </div>
  );
}

function Eintrag({ eintrag, groesse, formenByCode, turnierdatum, now, t }) {
  if (eintrag.type === 'kampf') {
    const k = eintrag.data;
    return (
      <div className={`adp-e adp-e--${groesse}`}>
        <div className="adp-e-kopf">
          <span className="adp-e-kat">{umbruchNachSchraegstrich(kategorieText(k.division_code, k.division_name || k.bracket_name))}</span>
          <span className="adp-e-runde">{rundenName(k, t)}</span>
        </div>
        <Kaempfer vorname={k.k1_vorname} nachname={k.k1_nachname} verein={k.k1_verein} gurt={k.k1_gurt} />
        <div className="adp-e-gegen">{t('aufruf.versus')}</div>
        <Kaempfer vorname={k.k2_vorname} nachname={k.k2_nachname} verein={k.k2_verein} gurt={k.k2_gurt} />
      </div>
    );
  }
  const s = eintrag.data;
  const status = getSlotStatus(s, turnierdatum, now);
  const zeit = s.start_time ? (s.end_time ? `${s.start_time}–${s.end_time}` : s.start_time) : '';
  const starter = formenByCode[s.division_code] ?? [];
  const zeigen = groesse === 'gross' ? starter.slice(0, 3) : [];
  const anzahl = starter.length || s.participant_count || 0;
  return (
    <div className={`adp-e adp-e--${groesse}${status === 'active' ? ' adp-e--aktiv' : ''}`}>
      <div className="adp-e-kopf">
        {zeit && <span className="adp-e-zeit">{zeit}</span>}
        <Verzug minuten={s.verzug_minuten} planzeit={s.start_time_plan} t={t} />
      </div>
      <div className="adp-e-katname">{umbruchNachSchraegstrich(kategorieText(s.division_code, s.division_name))}</div>
      {zeigen.map((p, i) => (
        <div key={i} className="adp-e-starter">
          <span className="adp-e-startnr">{p.startnummer}.</span> {p.vorname} {p.nachname}
        </div>
      ))}
      {anzahl > zeigen.length && (
        <div className="adp-e-anzahl">{t('aufruf.starters', { n: anzahl })}</div>
      )}
    </div>
  );
}

function MatteBody({ matte, running, ready, pending, zeitplan, formenByCode, turnierdatum, now, t }) {
  const nr = matte.nummer;
  const isLive = running.some(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
  const schlange = matteWarteschlange({ nr, ready, pending, zeitplan, turnierdatum, now });
  const [jetzt, naechstes] = schlange;
  const rest = Math.max(0, schlange.length - 2);

  // To-do 496: Wechselt „Jetzt“ oder „Als Nächstes“ auf einen anderen
  // Eintrag, oder wird ein wartender Kampf bereit (Zeitplan-Block aktiv),
  // leuchtet der Block kurz auf. Verglichen wird nur Schlüssel + Zustand:
  // der Sekundentakt und das 20-s-Polling ohne Änderung lösen nichts aus.
  const leuchtet = useHervorheben(
    [{ platz: 'jetzt', e: jetzt }, { platz: 'naechstes', e: naechstes }],
    (x) => x.platz,
    (x) => (x.e ? `${x.e.key}:${x.e.bereit ? 'bereit' : 'wartet'}` : ''),
  );
  const blockKlasse = (platz) => (leuchtet.has(platz) ? ' anim-leuchten' : '');

  if (!jetzt) {
    return (
      <div className="adp-col-body adp-col-body--idle">
        <div className="adp-col-idle">
          <div className="adp-col-idle-kanji">{isLive ? '試合' : '休'}</div>
          <div className="adp-col-idle-text">{isLive ? t('aufruf.fight_running') : t('aufruf.free')}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="adp-col-body">
      <section className={`adp-block adp-block--jetzt${blockKlasse('jetzt')}`} data-eintrag={jetzt.key}>
        <div className="adp-block-lbl">{t('aufruf.now')}</div>
        <div key={jetzt.key} className="anim-ein">
          <Eintrag eintrag={jetzt} groesse="gross" formenByCode={formenByCode} turnierdatum={turnierdatum} now={now} t={t} />
        </div>
      </section>
      {naechstes && (
        <section className={`adp-block adp-block--naechstes${blockKlasse('naechstes')}`} data-eintrag={naechstes.key}>
          <div className="adp-block-lbl">{t('aufruf.next')}</div>
          <div key={naechstes.key} className="anim-ein">
            <Eintrag eintrag={naechstes} groesse="mittel" formenByCode={formenByCode} turnierdatum={turnierdatum} now={now} t={t} />
          </div>
        </section>
      )}
      {rest > 0 && <div className="adp-rest">{t('aufruf.more', { n: rest })}</div>}
    </div>
  );
}

// ── Focus-Overlay: große Einzel-Ansicht ──────────────────────────────────────
function FocusOverlay({ item, formenByCode, turnierdatum, now, tatamis, t }) {
  if (!item) return null;
  const tatamiObj = tatamis.find(m => Number(m.nummer) === Number(item.tatami));
  const tatamiLabel = tatamiObj?.name && tatamiObj.name !== `Matte ${item.tatami}`
    ? tatamiObj.name
    : `${t('aufruf.mat')} ${item.tatami}`;

  if (item.type === 'kampf') {
    const k = item.data;
    return (
      <div className="adp-focus anim-leuchten">
        <div className="adp-focus-tatami">{tatamiLabel}</div>
        <div className="adp-focus-category">{kategorieText(k.division_code, k.division_name || k.bracket_name)}</div>
        <div className="adp-focus-round">{rundenName(k, t)}</div>
        <div className="adp-focus-vs">
          <div className="adp-focus-fighter adp-focus-fighter--left">
            <div className="adp-focus-name">{k.k1_vorname} {k.k1_nachname}</div>
            <div className="adp-focus-club">{k.k1_verein || '—'}</div>
            {k.k1_gurt && <GurtDot farbe={k.k1_gurt} />}
          </div>
          <div className="adp-focus-badge">VS</div>
          <div className="adp-focus-fighter adp-focus-fighter--right">
            <div className="adp-focus-name">{k.k2_vorname} {k.k2_nachname}</div>
            <div className="adp-focus-club">{k.k2_verein || '—'}</div>
            {k.k2_gurt && <GurtDot farbe={k.k2_gurt} />}
          </div>
        </div>
        {k.geplante_zeit && (() => {
          const cd = calcCountdown(k.geplante_zeit, now);
          return cd ? <div className={`adp-focus-timer adp-col-timer--${cd.state}`}>{cd.text}</div> : null;
        })()}
      </div>
    );
  }

  // Formen/Zeitplan-Slot
  const slot = item.data;
  const starter = formenByCode[slot.division_code] ?? [];
  const label = slot.start_time
    ? (slot.end_time ? `${slot.start_time} – ${slot.end_time} Uhr` : `${slot.start_time} Uhr`)
    : '';
  const cd = slot.start_time ? calcCountdownToTime(slot.start_time, turnierdatum, now) : null;

  return (
    <div className="adp-focus adp-focus--slot anim-leuchten">
      <div className="adp-focus-tatami">{tatamiLabel}</div>
      {label && <div className="adp-focus-time">{label}</div>}
      <Verzug minuten={slot.verzug_minuten} planzeit={slot.start_time_plan} t={t} gross />
      <div className="adp-focus-category">{kategorieText(slot.division_code, slot.division_name)}</div>
      {cd && <div className={`adp-focus-timer adp-col-timer--${cd.state}`}>{cd.text}</div>}
      {starter.length > 0 ? (
        <div className="adp-focus-starter-list">
          {starter.map((s, i) => (
            <div key={i} className="adp-focus-starter-row">
              <span className="adp-focus-starter-nr">{s.startnummer}.</span>
              <span className="adp-focus-starter-name">{s.vorname} {s.nachname}</span>
              {s.verein && <span className="adp-focus-starter-club">{s.verein}</span>}
            </div>
          ))}
        </div>
      ) : slot.participant_count > 0 ? (
        <div className="adp-focus-count">{slot.participant_count} Teilnehmer</div>
      ) : null}
    </div>
  );
}

// ── Hauptkomponente ──────────────────────────────────────────────────────────
export default function AufrufDisplayPage() {
  const { turnierId }         = useParams();
  const [searchParams]        = useSearchParams();
  const { t }                 = useTranslation();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [now, setNow]         = useState(Date.now());
  const timerRef              = useRef(null);

  // Sekundengenauer Tick
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Die Ping-Pong-Laufschrift (bis 18.10.2026) ist weg: je Matte stehen nur
  // noch zwei Einträge, es gibt nichts mehr zu scrollen. Ein Schirm, dessen
  // Inhalt wandert, liest man in der Halle nicht.
  const viewportRef  = useRef(null);
  const contentRef   = useRef(null);

  // Filter: optional ?matte=1,2,3
  const matteParam = searchParams.get('matte') || '';
  const matteFilter = searchParams.get('matte')
    ? searchParams.get('matte').split(',').map(Number)
    : null;

  const load = useCallback(() => {
    fetch(`/api/brackets/turnier/${turnierId}/aufruf?geraet=hallenschirm`)
      .then(r => r.json())
      .then(d => {
        if (d.success) { setData(d); setLastUpdate(new Date()); setError(null); }
        else setError('Daten konnten nicht geladen werden.');
      })
      .catch(() => setError('Verbindungsfehler.'))
      .finally(() => setLoading(false));
  }, [turnierId]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 20000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  // Schreiber-Call State pollen
  // Mehrere Matten können gleichzeitig aufgerufen haben: der Schirm zeigt
  // sie im Wechsel (alle 6 s), statt dass der neueste die anderen verdrängt.
  const [manualCalls, setManualCalls] = useState([]);
  const [callIndex, setCallIndex] = useState(0);
  useEffect(() => {
    if (manualCalls.length < 2) { setCallIndex(0); return undefined; }
    const t = setInterval(() => setCallIndex((i) => (i + 1) % manualCalls.length), 6000);
    return () => clearInterval(t);
  }, [manualCalls.length]);
  const manualCall = manualCalls.length ? manualCalls[callIndex % manualCalls.length] : null;
  useEffect(() => {
    const poll = () => {
      // Aufrufe gelten je Matte (18.10.2026); ein gefilterter Schirm
      // (?matte=1,2) zeigt nur die Aufrufe seiner Matten, der neueste zuerst.
      const q = matteParam ? `?matte=${encodeURIComponent(matteParam)}` : '';
      fetch(`/api/brackets/turnier/${turnierId}/aufruf-state${q}`)
        .then(r => r.json())
        .then(d => setManualCalls(Array.isArray(d.states) ? d.states : (d.state ? [d.state] : [])))
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => clearInterval(id);
  }, [turnierId, matteParam]);

  const turnierdatum = data?.turnier?.datum ?? null;

  // Bis 18.10.2026 blendete der Schirm alle 8 s die nächsten zwei Einträge
  // groß über alles. Seit „Jetzt“ und „Als Nächstes“ je Matte groß stehen,
  // verdeckte das nur, was ohnehin dasteht. Groß eingeblendet wird nur noch
  // ein Aufruf aus der Aufruf-Steuerung; ein Klick blendet ihn hier aus.
  const [ausgeblendet, setAusgeblendet] = useState(null);

  // Welches Item im Focus zeigen?
  const focusItem = useMemo(() => {
    // Manueller Schreiber-Call hat Vorrang
    if (manualCall?.division_code) {
      const fromSlot = (data?.zeitplan ?? []).find(s => s.division_code === manualCall.division_code);
      if (fromSlot) return { type: 'slot', data: fromSlot, tatami: fromSlot.ring };
      const fromKampf = (data?.ready ?? []).find(k => k.division_code === manualCall.division_code)
                     || (data?.pending ?? []).find(k => k.division_code === manualCall.division_code);
      if (fromKampf) return { type: 'kampf', data: fromKampf, tatami: fromKampf.tatami_nummer ?? fromKampf.matte_nummer };
    }
    return null;
  }, [manualCall, data]);

  const showFocus = manualCall !== null && manualCall?.division_code !== ausgeblendet;

  // Tatami-Liste
  const tatamis = (() => {
    if (!data) return [];
    let list = [];
    if (data.kampfflaechen?.length) {
      list = matteFilter
        ? data.kampfflaechen.filter(kf => matteFilter.includes(kf.nummer))
        : data.kampfflaechen;
    } else {
      const zeitplanRings = (data.zeitplan ?? []).map(s => Number(s.ring)).filter(Boolean);
      const nrs = [...new Set([
        ...data.running.map(k => k.tatami_nummer ?? k.matte_nummer),
        ...data.ready.map(k => k.tatami_nummer ?? k.matte_nummer),
        ...data.pending.map(k => k.tatami_nummer ?? k.matte_nummer),
        ...zeitplanRings,
      ].filter(Boolean))].sort((a, b) => a - b);
      const filtered = matteFilter ? nrs.filter(n => matteFilter.includes(n)) : nrs;
      list = filtered.map(nr => ({ nummer: nr, name: null, farbe: null }));
    }
    const seen = new Set();
    return list.filter(kf => { if (seen.has(kf.nummer)) return false; seen.add(kf.nummer); return true; });
  })();

  // Formen-Starter gruppiert nach division_code
  const formenByCode = useMemo(() => {
    const map = {};
    for (const s of (data?.formenStarter ?? [])) {
      if (!map[s.division_code]) map[s.division_code] = [];
      map[s.division_code].push(s);
    }
    return map;
  }, [data]);

  // Header-Countdown pro Tatami
  function tatamiCountdown(nr) {
    if (!data) return null;
    const readyHere  = data.ready.filter(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
    const pendingHere = data.pending.filter(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
    const slotsHere  = (data.zeitplan ?? []).filter(s => Number(s.ring) === Number(nr));
    const nextFight  = (readyHere[0] || pendingHere[0])?.geplante_zeit ?? null;
    let cd = calcCountdown(nextFight, now);
    if (!cd) {
      const upcoming = slotsHere
        .filter(s => getSlotStatus(s, turnierdatum, now) === 'upcoming')
        .sort((a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? ''))[0];
      if (upcoming?.start_time) cd = calcCountdownToTime(upcoming.start_time, turnierdatum, now);
    }
    return cd;
  }

  // To-do 496: Matte geht auf „läuft“ → Kopf leuchtet kurz auf (nur beim
  // Einschalten; das Ausgehen bleibt still).
  const mattenLive = useHervorheben(
    data ? tatamis : null,
    (m) => m.nummer,
    (m) => (data.running.some(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(m.nummer)) ? 'live' : ''),
  );

  const noMatteReady   = data?.ready.filter(k => !(k.tatami_nummer ?? k.matte_nummer)) ?? [];
  const noMattePending = data?.pending.filter(k => !(k.tatami_nummer ?? k.matte_nummer)).slice(0, 5) ?? [];
  // Warteliste ohne Matte (To-do 496): neue Kämpfe blenden ein, vergebene
  // aus; wird ein wartender Kampf bereit, leuchtet er kurz auf.
  const warteliste = useAnimierteListe(
    data ? [...noMatteReady.map(k => ({ k, bereit: true })), ...noMattePending.map(k => ({ k, bereit: false }))] : null,
    (x) => x.k.kampf_id,
    (x) => (x.bereit ? 'bereit' : 'wartet'),
  );

  if (loading) return (
    <div className="adp-page">
      <div className="adp-bg-kanji" aria-hidden>武</div>
      <div className="adp-center"><div className="adp-spinner" /><span>{t('aufruf.loading')}</span></div>
    </div>
  );

  if (error) return (
    <div className="adp-page">
      <div className="adp-center adp-error">{error}</div>
    </div>
  );

  const allEmpty = tatamis.length === 0 && noMatteReady.length === 0 && noMattePending.length === 0;

  return (
    <div className="adp-page">
      <div className="adp-bg-kanji" aria-hidden>武</div>
      <div className="adp-lang-switch"><LanguageSwitch /></div>

      <header className="adp-header">
        <div className="adp-header-org">TIGER &amp; DRAGON ASSOCIATION INTERNATIONAL</div>
        <h1 className="adp-header-title">KÄMPFER-AUFRUF</h1>
        {data?.turnier && <div className="adp-header-turnier">{data.turnier.name}</div>}
        <div className="adp-header-time">
          {t('aufruf.updated')}: {lastUpdate?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      </header>

      {/* Focus-Overlay (Schreiber-Call oder Auto-Cycle) */}
      {/* key: ein neuer Aufruf (oder der Wechsel zwischen mehreren) hängt
          die Einblendung neu ein, so läuft Einblenden + Aufleuchten erneut.
          Dasselbe Polling-Ergebnis behält den Schlüssel, nichts flackert. */}
      {showFocus && focusItem && (
        <div key={`${manualCall?.division_code ?? ''}|${focusItem.tatami ?? ''}`} className="adp-focus-wrap" onClick={() => setAusgeblendet(manualCall?.division_code ?? null)}>
          <FocusOverlay
            item={focusItem}
            formenByCode={formenByCode}
            turnierdatum={turnierdatum}
            now={now}
            tatamis={tatamis}
            t={t}
          />
          <div className="adp-focus-hint">Klicken zum Schließen</div>
        </div>
      )}

      {/* ── Feste Header-Zeile (scrollt NICHT mit) ── */}
      {!allEmpty && (
        <div className="adp-headers-row">
          {tatamis.map(matte => {
            const nr = matte.nummer;
            const name = matte.name && matte.name !== `Matte ${nr}` ? matte.name : null;
            const isLive = data.running.some(k => Number(k.tatami_nummer ?? k.matte_nummer) === Number(nr));
            const cd = tatamiCountdown(nr);
            return (
              <div
                key={matte.kampfflaeche_id ?? nr}
                className={`adp-col-header${isLive ? ' adp-col-header--live' : ''}${isLive && mattenLive.has(String(nr)) ? ' anim-leuchten' : ''}`}
                style={matte.farbe ? {'--matte-farbe': matte.farbe} : {}}
              >
                <span className="adp-col-num">{nr}</span>
                <div className="adp-col-title">
                  <span className="adp-col-mat-label">{name || t('aufruf.mat')}</span>
                  {isLive && <span className="adp-col-live-dot" />}
                </div>
                {cd && <div className={`adp-col-timer adp-col-timer--${cd.state}`}>{cd.text}</div>}
              </div>
            );
          })}
          {(noMatteReady.length > 0 || noMattePending.length > 0) && (
            <div className="adp-col-header adp-col-header--warteliste">
              <span className="adp-col-num">–</span>
              <div className="adp-col-title">
                <span className="adp-col-mat-label">{t('aufruf.no_mat')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Scrollbarer Board-Inhalt ── */}
      <div className="adp-board" ref={viewportRef}>
        {allEmpty ? (
          <div className="adp-empty">
            <div>{t('aufruf.no_fights')}</div>
          </div>
        ) : (
          <div className="adp-board-inner" ref={contentRef}>
            {tatamis.map(matte => (
              <MatteBody
                key={matte.kampfflaeche_id ?? matte.nummer}
                matte={matte}
                running={data.running}
                ready={data.ready}
                pending={data.pending}
                zeitplan={data.zeitplan ?? []}
                formenByCode={formenByCode}
                turnierdatum={turnierdatum}
                now={now}
                t={t}
              />
            ))}

            {(noMatteReady.length > 0 || noMattePending.length > 0) && (
              <div className="adp-col-body adp-col-body--warteliste" ref={warteliste.behaelterRef}>
                {warteliste.zeilen.map(z => (
                  <div key={z.schluessel} data-flip={z.schluessel}
                       className={`adp-col-section adp-col-section--${z.eintrag.bereit ? 'ready' : 'pending'} ${warteliste.klasse(z)}`}>
                    {z.eintrag.bereit && <div className="adp-col-section-label adp-label--ready">{t('aufruf.ready')}</div>}
                    <KampfRow kampf={z.eintrag.k} t={t} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <InfoLeiste info={data?.hallen_info} turnierId={turnierId} t={t} />
    </div>
  );
}
